import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { 
  WeeklyAvailability, 
  AvailabilitySlot, 
  UserAvailability 
} from '../../interfaces/activity-schedule.interface';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
  selector: 'app-availability-management',
  templateUrl: './availability-management.component.html',
  styleUrls: ['./availability-management.component.scss']
})
export class AvailabilityManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  availabilityForm!: FormGroup;
  currentUserId = 0;
  isLoading = false;
  isSaving = false;
  
  dayNames = this.activityScheduleService.getDayNames();
  timeSlots = this.activityScheduleService.generateTimeSlots();
  dayControls: { key: string; index: number }[] = [];
  
  // Tab navigation
  selectedDayIndex = 0; // Currently selected day tab (0-6)
  
  // Date range form
  dateRangeForm!: FormGroup;
  
  // Default working hours
  defaultWorkingHours = {
    start: '09:00',
    end: '17:00'
  };

  constructor(
    private fb: FormBuilder,
    private activityScheduleService: ActivityScheduleService,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {
    this.initializeForm();
    this.initializeDateRangeForm();
  }

  async ngOnInit(): Promise<void> {
    await this.loadUserContext();
    this.loadCurrentAvailability();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadUserContext(): Promise<void> {
    try {
      const user = await this.authService.getCurrentUser();
      console.log('Current user from AuthService:', user);
      
      if (user && user.id_user) {
        this.currentUserId = user.id_user;
      } else if (user && user.id) {
        this.currentUserId = user.id;
      } else {
        console.warn('No user ID found in user object:', user);
        this.currentUserId = 1;
      }
      
      console.log('Set currentUserId to:', this.currentUserId);
    } catch (error) {
      console.error('Error loading current user:', error);
      this.currentUserId = 1;
    }
  }

  private initializeForm(): void {
    // Create a FormGroup with each day as a FormGroup keyed by day index
    const dayGroups: { [key: string]: FormGroup } = {};
    
    for (let day = 0; day < 7; day++) {
      dayGroups[day.toString()] = this.fb.group({
        enabled: [day < 5], // Monday-Friday enabled by default
        slots: this.fb.array(day < 5 ? [this.createTimeSlotFormGroup()] : [])
      });
    }
    
    this.availabilityForm = this.fb.group(dayGroups);
    this.updateDayControls();
    console.log('Form initialized:', this.availabilityForm.value);
  }

  private initializeDateRangeForm(): void {
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + 3);

    this.dateRangeForm = this.fb.group({
      startDate: [today],
      endDate: [threeMonthsLater],
      isActive: [true] // Whether the date range is enabled
    });
  }

  private createTimeSlotFormGroup(): FormGroup {
    return this.fb.group({
      start_time: [this.defaultWorkingHours.start, Validators.required],
      end_time: [this.defaultWorkingHours.end, Validators.required],
      notes: ['']
    }, { validators: this.timeSlotValidator });
  }

  private timeSlotValidator(group: FormGroup) {
    const startTime = group.get('start_time')?.value;
    const endTime = group.get('end_time')?.value;
    
    if (startTime && endTime && startTime >= endTime) {
      return { invalidTimeRange: true };
    }
    
    return null;
  }

  getDayFormGroup(dayIndex: number): FormGroup {
    return this.availabilityForm.get(dayIndex.toString()) as FormGroup;
  }

  getSlotsFormArray(dayIndex: number): FormArray {
    return this.getDayFormGroup(dayIndex)?.get('slots') as FormArray;
  }

  loadCurrentAvailability(): void {
    if (!this.currentUserId) {
      console.warn('No current user ID available, cannot load availability');
      return;
    }
    
    console.log('Loading availability for user ID:', this.currentUserId);
    this.isLoading = true;
    
    this.activityScheduleService.getUserAvailability(this.currentUserId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (availability) => {
        console.log('Loaded availability:', availability);
        this.populateFormWithAvailability(availability || {});
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading availability:', error);
        let errorMessage = 'Eroare la încărcarea programului';
        if (error.message) {
          errorMessage += ': ' + error.message;
        } else if (error.error && error.error.message) {
          errorMessage += ': ' + error.error.message;
        }
        
        this.notificationService.warningSwal('Eroare', errorMessage, 'error');
        this.isLoading = false;
      }
    });
  }

  private populateFormWithAvailability(availability: WeeklyAvailability): void {
    console.log('Populating form with availability:', availability);
    
    // Handle date range data if present
    const availabilityWithDateRange = availability as any;
    if (availabilityWithDateRange.date_range) {
      const dateRange = availabilityWithDateRange.date_range;
      console.log('Found date range data:', dateRange);
      
      const dateRangeValue: any = {
        isActive: dateRange.is_active || false
      };
      
      if (dateRange.availability_start_date) {
        dateRangeValue.startDate = new Date(dateRange.availability_start_date);
      }
      if (dateRange.availability_end_date) {
        dateRangeValue.endDate = new Date(dateRange.availability_end_date);
      }
      
      this.dateRangeForm.patchValue(dateRangeValue);
      console.log('Date range form updated:', this.dateRangeForm.value);
    }
    
    // Process each day (backend returns keys 1-7, but form uses 0-6)
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const backendDayKey = dayIndex + 1; // Backend uses 1-7, form uses 0-6
      const dayAvailability = availability[backendDayKey] || [];
      const dayFormGroup = this.getDayFormGroup(dayIndex);
      
      if (!dayFormGroup) {
        console.error(`Day form group not found for day index ${dayIndex}`);
        continue;
      }

      if (dayAvailability.length > 0) {
        // Enable the day and populate slots
        dayFormGroup.patchValue({ enabled: true });
        
        const slotsArray = this.getSlotsFormArray(dayIndex);
        slotsArray.clear();
        
        dayAvailability.forEach(slot => {
          slotsArray.push(this.fb.group({
            start_time: [slot.start_time, Validators.required],
            end_time: [slot.end_time, Validators.required],
            notes: [slot.notes || '']
          }, { validators: this.timeSlotValidator }));
        });
      } else {
        // Disable the day and clear slots
        dayFormGroup.patchValue({ enabled: false });
        const slotsArray = this.getSlotsFormArray(dayIndex);
        slotsArray.clear();
      }
    }
    
    this.updateDayControls();
    console.log('Form populated:', this.availabilityForm.value);
  }

  onDayEnabledChange(dayIndex: number): void {
    const dayGroup = this.getDayFormGroup(dayIndex);
    const enabled = dayGroup.get('enabled')?.value;
    
    const slotsArray = this.getSlotsFormArray(dayIndex);
    
    if (!enabled) {
      slotsArray.clear();
    } else {
      if (slotsArray.length === 0) {
        slotsArray.push(this.createTimeSlotFormGroup());
      }
    }
  }

  addTimeSlot(dayIndex: number): void {
    const slotsArray = this.getSlotsFormArray(dayIndex);
    
    // Smart start time: use end time of last slot as start time for new slot
    let defaultStartTime = this.defaultWorkingHours.start;
    let defaultEndTime = this.defaultWorkingHours.end;
    
    if (slotsArray.length > 0) {
      const lastSlot = slotsArray.at(slotsArray.length - 1) as FormGroup;
      const lastEndTime = lastSlot.get('end_time')?.value;
      if (lastEndTime) {
        defaultStartTime = lastEndTime;
        // Add 1 hour to the start time for default end time
        const [hours, minutes] = lastEndTime.split(':').map(Number);
        const newEndHour = Math.min(hours + 1, 23);
        defaultEndTime = `${newEndHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    
    const newSlot = this.fb.group({
      start_time: [defaultStartTime, Validators.required],
      end_time: [defaultEndTime, Validators.required],
      notes: ['']
    }, { validators: this.timeSlotValidator });
    
    slotsArray.push(newSlot);
  }

  removeTimeSlot(dayIndex: number, slotIndex: number): void {
    const slotsArray = this.getSlotsFormArray(dayIndex);
    if (slotsArray.length > 1) {
      slotsArray.removeAt(slotIndex);
    } else {
      this.notificationService.warningSwal('Atenție', 'Trebuie să existe cel puțin un interval de timp pentru fiecare zi activă.', 'warning');
    }
  }

  setStandardWorkingHours(): void {
    // Set Mon-Fri 9:00-17:00
    for (let dayIndex = 0; dayIndex < 5; dayIndex++) {
      const dayGroup = this.getDayFormGroup(dayIndex);
      dayGroup.patchValue({ enabled: true });
      
      const slotsArray = this.getSlotsFormArray(dayIndex);
      slotsArray.clear();
      slotsArray.push(this.fb.group({
        start_time: ['09:00', Validators.required],
        end_time: ['17:00', Validators.required],
        notes: ['Program standard de lucru']
      }, { validators: this.timeSlotValidator }));
    }
    
    // Disable weekends
    for (let dayIndex = 5; dayIndex < 7; dayIndex++) {
      const dayGroup = this.getDayFormGroup(dayIndex);
      dayGroup.patchValue({ enabled: false });
      
      const slotsArray = this.getSlotsFormArray(dayIndex);
      slotsArray.clear();
    }
    this.updateDayControls();
  }

  clearAllAvailability(): void {
    this.notificationService.confirmSwal(
      'Confirmare ștergere',
      'Sigur doriți să ștergeți toată disponibilitatea? Această acțiune nu poate fi anulată.',
      'Da, șterge tot!',
      'Anulează'
    ).then((result: any) => {
      if (result.isConfirmed) {
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          const dayGroup = this.getDayFormGroup(dayIndex);
          dayGroup.patchValue({ enabled: false });
          
          const slotsArray = this.getSlotsFormArray(dayIndex);
          slotsArray.clear();
        }
        this.updateDayControls();
        this.notificationService.successSwal('Șters!', 'Toată disponibilitatea a fost ștearsă.');
      }
    });
  }

  onSubmit(): void {
    if (this.availabilityForm.invalid) {
      this.notificationService.warningSwal('Eroare', 'Vă rugăm să corectați erorile din formular.', 'error');
      return;
    }

    if (!this.currentUserId) {
      this.notificationService.warningSwal('Eroare', 'Nu s-a putut identifica utilizatorul curent. Vă rugăm să vă reautentificați.', 'error');
      return;
    }

    this.isSaving = true;
    const formData = this.availabilityForm.value;
    const availabilitySlots: AvailabilitySlot[] = [];

    // Convert form data to API format
    Object.keys(formData).forEach((dayKey, index) => {
      const dayIndex = parseInt(dayKey);
      const dayData = formData[dayKey];
      
      if (dayData.enabled && dayData.slots && dayData.slots.length > 0) {
        dayData.slots.forEach((slot: any) => {
          availabilitySlots.push({
            day_of_week: dayIndex + 1, // Backend expects 1-7, form uses 0-6
            start_time: slot.start_time,
            end_time: slot.end_time,
            notes: slot.notes || undefined
          });
        });
      }
    });

    // Get date range data
    const dateRangeData = this.dateRangeForm.value;
    const requestData: any = {
      availability: availabilitySlots
    };

    // Add date range if active
    if (dateRangeData.isActive) {
      requestData.availability_start_date = this.formatDateForAPI(dateRangeData.startDate);
      requestData.availability_end_date = this.formatDateForAPI(dateRangeData.endDate);
    }

    console.log('Saving availability for user ID:', this.currentUserId);
    console.log('Availability data to save:', requestData);

    this.activityScheduleService.setUserAvailabilityWithDateRange(this.currentUserId, requestData).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        console.log('Availability saved successfully:', response);
        this.notificationService.successSwal('Succes', 'Programul de disponibilitate a fost salvat cu succes!');
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving availability:', error);
        let errorMessage = 'Eroare necunoscută';
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        this.notificationService.warningSwal('Eroare', 'Eroare la salvarea programului: ' + errorMessage, 'error');
        this.isSaving = false;
      }
    });
  }

  // Utility methods
  getDayName(dayIndex: number): string {
    return this.dayNames[dayIndex + 1] || `Zi ${dayIndex + 1}`;
  }

  hasValidationErrors(dayIndex: number, slotIndex: number): boolean {
    const slotsArray = this.getSlotsFormArray(dayIndex);
    const slotGroup = slotsArray?.at(slotIndex) as FormGroup;
    return slotGroup?.hasError('invalidTimeRange') || false;
  }

  getValidationError(dayIndex: number, slotIndex: number): string {
    if (this.hasValidationErrors(dayIndex, slotIndex)) {
      return 'Ora de sfârșit trebuie să fie după ora de început';
    }
    return '';
  }

  getTotalHoursForDay(dayIndex: number): number {
    const dayGroup = this.getDayFormGroup(dayIndex);
    if (!dayGroup?.get('enabled')?.value) return 0;

    const slots = dayGroup.get('slots')?.value || [];
    let totalMinutes = 0;

    slots.forEach((slot: any) => {
      if (slot.start_time && slot.end_time) {
        const start = this.timeToMinutes(slot.start_time);
        const end = this.timeToMinutes(slot.end_time);
        if (end > start) {
          totalMinutes += (end - start);
        }
      }
    });

    return Math.round((totalMinutes / 60) * 10) / 10;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  getTotalWeeklyHours(): number {
    let total = 0;
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      total += this.getTotalHoursForDay(dayIndex);
    }
    return Math.round(total * 10) / 10;
  }

  getActiveDaysCount(): number {
    let count = 0;
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayGroup = this.getDayFormGroup(dayIndex);
      if (dayGroup?.get('enabled')?.value) {
        count++;
      }
    }
    return count;
  }

  // Update day controls array - call this when form structure changes
  private updateDayControls(): void {
    this.dayControls = Object.keys(this.availabilityForm.controls).map((key) => ({
      key,
      index: parseInt(key) // Use the actual key as index (0-6 for days)
    }));
  }

  // Template helper - returns cached day controls array  
  getDayControls() {
    return this.dayControls;
  }

  // Tab navigation methods
  selectDay(dayIndex: number): void {
    this.selectedDayIndex = dayIndex;
  }

  getSelectedDayName(): string {
    return this.getDayName(this.selectedDayIndex);
  }

  isDayEnabled(dayIndex: number): boolean {
    const dayGroup = this.getDayFormGroup(dayIndex);
    return dayGroup?.get('enabled')?.value || false;
  }

  toggleDayEnabled(dayIndex: number): void {
    this.onDayEnabledChange(dayIndex);
  }

  // Date range utility methods
  private formatDateForAPI(date: Date): string {
    if (!date) return '';
    // Use local timezone to avoid date shifting issues
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onDateRangeToggle(): void {
    const isActive = this.dateRangeForm.get('isActive')?.value;
    if (isActive) {
      this.dateRangeForm.get('startDate')?.enable();
      this.dateRangeForm.get('endDate')?.enable();
    } else {
      this.dateRangeForm.get('startDate')?.disable();
      this.dateRangeForm.get('endDate')?.disable();
    }
  }
}